import type { FlowTemplateCategory } from "./templates";

// Local translations
export const T = {
  en_GB: {
    createFromTemplate: "Create Flow from Template",
    templateDialogDescription:
      "Choose a pre-configured MiFID II flow template to get started quickly.",
    configureTemplate: "Configure",
    configureTemplateDescription: "Configure your flow settings before creating.",
    searchTemplates: "Search templates...",
    noTemplatesFound: "No templates found",
    multiGoal: "Multi-Goal",
    publicStart: "Public Start",
    requirements: "Requirements:",
    riskModelRequired: "Risk model required",
    portfolioCollectionRequired: "Portfolio collection required",
    financialSituationModelRequired: "Financial situation model required",
    pages: "Pages",
    public: "Public",
    internal: "Internal",
    goal: "Goal",
    global: "Global",
    portfolio: "Portfolio",
    selectTemplatePrompt: "Select a template from the list to see details",
    cancel: "Cancel",
    next: "Next",
    back: "Back",
    flowName: "Flow Name",
    clientOnboardingPlaceholder: "e.g., Client Onboarding",
    riskModel: "Risk Model",
    selectRiskModel: "Select risk model",
    noRiskModel: "No risk model",
    requiredForTemplate: "Required for this template",
    requiredForRiskAssessment: "Required for risk assessment and portfolio features",
    // QL-212 — the template places the risk assessment where this model captures nothing
    riskScopeMismatch:
      "This risk model captures every risk input on a different page type than this template places the risk assessment on, so it would show nothing. Pick another risk model, or move the component after creating the flow.",
    portfolioCollection: "Portfolio Collection",
    selectPortfolioCollection: "Select portfolio collection",
    noCollection: "No collection",
    selectRiskModelFirst: "Select a risk model first",
    requiredForPortfolioFeatures: "Required for portfolio display and analysis features",
    financialSituationModel: "Financial Situation Model",
    selectFinancialSituationModel: "Select financial situation model",
    noFinancialSituationModel: "No financial situation model",
    requiredForFinancialSituation: "Required for financial situation assessment",
    create: "Create",
    failedToCreateFromTemplate: "Failed to create flow from template",
    flowNameRequired: "Flow name is required",
    language: "Language",
    selectLanguage: "Select language",
    missingPrefix: "Missing:",
    missingTimeseries: "requires timeseries",
    missingAssetAllocation: "requires asset allocation",
    // QL-372 — the flow currency, fixed after creation; taken from the collection when one is chosen
    currency: "Currency",
    currencyHint: "Amounts in this flow use this currency. It can't be changed later.",
    currencyFromCollection: "Taken from the portfolio collection. It can't be changed later.",
    collectionsInCurrency: (code: string) => `Only collections in ${code} are listed.`,
    categories: {
      execution: "Order Execution",
      "advisory-single": "MiFID Advice - Single Goal",
      "advisory-multi": "MiFID Advice - Multi-Goal",
      "self-service": "Self-Service / Public Access",
      specialized: "Specialized Flows",
    } satisfies Record<FlowTemplateCategory, string>,
  },
  nb_NO: {
    createFromTemplate: "Opprett flyt fra mal",
    templateDialogDescription:
      "Velg en forhåndskonfigurert MiFID II-flytmal for å komme raskt i gang.",
    configureTemplate: "Konfigurer",
    configureTemplateDescription: "Konfigurer flytinnstillingene før du oppretter.",
    searchTemplates: "Søk i maler...",
    noTemplatesFound: "Ingen maler funnet",
    multiGoal: "Flere mål",
    publicStart: "Offentlig start",
    requirements: "Krav:",
    riskModelRequired: "Risikomodell påkrevd",
    portfolioCollectionRequired: "Porteføljesamling påkrevd",
    financialSituationModelRequired: "Finansiell situasjonsmodell påkrevd",
    pages: "Sider",
    public: "Offentlig",
    internal: "Intern",
    goal: "Mål",
    global: "Global",
    portfolio: "Portefølje",
    selectTemplatePrompt: "Velg en mal fra listen for å se detaljer",
    cancel: "Avbryt",
    next: "Neste",
    back: "Tilbake",
    flowName: "Flytnavn",
    clientOnboardingPlaceholder: "f.eks. Kunde-onboarding",
    riskModel: "Risikomodell",
    selectRiskModel: "Velg risikomodell",
    noRiskModel: "Ingen risikomodell",
    requiredForTemplate: "Påkrevd for denne malen",
    requiredForRiskAssessment: "Påkrevd for risikovurdering og porteføljefunksjoner",
    // QL-212 — malen plasserer risikovurderingen der denne modellen ikke fanger noe
    riskScopeMismatch:
      "Denne risikomodellen fanger alle risikoinndata på en annen sidetype enn den malen plasserer risikovurderingen på, så den ville ikke vist noe. Velg en annen risikomodell, eller flytt komponenten etter at flyten er opprettet.",
    portfolioCollection: "Porteføljesamling",
    selectPortfolioCollection: "Velg porteføljesamling",
    noCollection: "Ingen samling",
    selectRiskModelFirst: "Velg en risikomodell først",
    requiredForPortfolioFeatures: "Påkrevd for porteføljevisning og analysefunksjoner",
    financialSituationModel: "Finansiell situasjonsmodell",
    selectFinancialSituationModel: "Velg finansiell situasjonsmodell",
    noFinancialSituationModel: "Ingen finansiell situasjonsmodell",
    requiredForFinancialSituation: "Påkrevd for vurdering av finansiell situasjon",
    create: "Opprett",
    failedToCreateFromTemplate: "Kunne ikke opprette flyt fra mal",
    flowNameRequired: "Flytnavn er påkrevd",
    language: "Språk",
    selectLanguage: "Velg språk",
    missingPrefix: "Mangler:",
    missingTimeseries: "krever tidsserie",
    missingAssetAllocation: "krever aktivaallokering",
    currency: "Valuta",
    currencyHint: "Beløp i denne flyten bruker denne valutaen. Den kan ikke endres senere.",
    currencyFromCollection: "Hentet fra porteføljesamlingen. Den kan ikke endres senere.",
    collectionsInCurrency: (code: string) => `Bare samlinger i ${code} vises.`,
    categories: {
      execution: "Ordreformidling",
      "advisory-single": "MiFID-rådgivning - Enkelt mål",
      "advisory-multi": "MiFID-rådgivning - Flere mål",
      "self-service": "Selvbetjening / Offentlig tilgang",
      specialized: "Spesialiserte flyter",
    } satisfies Record<FlowTemplateCategory, string>,
  },
} as const;
